import axios from "axios";

const OVERRIDE_KEY = "__twilanswerAssistantAvailableOverride";
const APPLY_EVENT = "twilanswer-assistant-availability-override";

/**
 * Check whether Twilanswer's ElevenLabs account has usable credits.
 * Public backend endpoint — no auth header required.
 *
 * Dev console overrides (no reload needed after calling apply):
 *   window.__twilanswerForceAssistantUnavailable()  // exhausted
 *   window.__twilanswerForceAssistantAvailable()    // clear override + recheck
 *   window.__twilanswerAssistantAvailableOverride = { available: false, credit_status: "exhausted" }
 *
 * @param {{ fresh?: boolean }} [opts]
 * @returns {Promise<{ available: boolean, credit_status: string }>}
 */
export async function fetchAssistantAvailable({ fresh = false } = {}) {
  if (typeof window !== "undefined" && window[OVERRIDE_KEY]) {
    const override = window[OVERRIDE_KEY];
    return {
      available: override?.available === true,
      credit_status:
        typeof override?.credit_status === "string"
          ? override.credit_status
          : "unknown",
    };
  }

  const base = process.env.REACT_APP_BACKEND_URL || "";
  const url = `${base}assistant/available`;
  try {
    const { data } = await axios.get(url, {
      params: fresh ? { fresh: "1" } : undefined,
      timeout: 10_000,
    });
    return {
      available: data?.available === true,
      credit_status:
        typeof data?.credit_status === "string"
          ? data.credit_status
          : "unknown",
    };
  } catch {
    return { available: false, credit_status: "unknown" };
  }
}

export function isCreditsExhausted(availability) {
  return availability?.credit_status === "exhausted";
}

/** Install console helpers for local UX testing. No-op outside the browser. */
export function installAssistantAvailabilityDebugHelpers() {
  if (typeof window === "undefined") return;

  window.__twilanswerForceAssistantUnavailable = (
    creditStatus = "exhausted"
  ) => {
    window[OVERRIDE_KEY] = {
      available: false,
      credit_status: creditStatus,
    };
    window.dispatchEvent(
      new CustomEvent(APPLY_EVENT, {
        detail: { ...window[OVERRIDE_KEY], where: "console_override" },
      })
    );
    // eslint-disable-next-line no-console
    console.info(
      "[assistant] Forced unavailable:",
      window[OVERRIDE_KEY],
      "— open Need a Hand? / Setup Assistant to see the panel"
    );
    return window[OVERRIDE_KEY];
  };

  window.__twilanswerForceAssistantAvailable = async () => {
    delete window[OVERRIDE_KEY];
    const result = await fetchAssistantAvailable({ fresh: true });
    window.dispatchEvent(
      new CustomEvent(APPLY_EVENT, {
        detail: { ...result, where: "console_clear_override" },
      })
    );
    // eslint-disable-next-line no-console
    console.info("[assistant] Cleared override; live availability:", result);
    return result;
  };

  window.__twilanswerAssistantAvailabilityEvent = APPLY_EVENT;
}
